import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getFinalLocalizationContext } from './utils/getFinalLocalizationContext';
import { getPackageBaseUrl } from 'owa-config';
import { initialize } from 'owa-strings';
import { initializeTranslationsForOwaDateTime } from './utils/initializeTranslationsForOwaDateTime';
import { measureFunctionPerf } from 'mini-performance/lib/utils/measureFunctionPerf';
import { setDocumentDirection } from 'mini-common-bootstrap/lib/setDocumentDirection';
import { traceBootstrapDiagnostics } from 'mini-common-bootstrap/lib/traceBootstrapDiagnostics';
import { updateDateTime } from 'owa-shared-bootstrap/lib/initializeDateTime';
import { urlResolve } from 'mini-common-utils/lib/utils/urlResolve';
import { waitForCalendarStrings } from './utils/waitForCalendarStrings';
import type OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import type { Culture } from 'owa-shared-localization';
import type { OwaWorkload } from 'owa-workloads';
import {
    getUserFastBootSettings,
    identifyUserFastBootSettingsChanges,
} from 'mini-early-processing/lib/userFastBootSettingsUtils';

let bootLocalizationPromise = null;
let runAfterBootLocalizationSet = false;
let fastBootSettings = null;

export function getInitializeBootLocalization(
    workload: OwaWorkload,
    runAfterBootLocalization?: () => void
) {
    return async function (locale: string, culture: Culture, dir?: 'ltr' | 'rtl') {
        let changes = null;
        const finalLocalizationParams = await getFinalLocalizationContext(locale, culture, dir);

        if (bootLocalizationPromise) {
            // This flow will be only entered if the method was called second time
            // This will happen when bootstrap flow attempts to call this method again.
            // Lets find the changes and decide if localization/datetime again needs to be done
            changes = identifyUserFastBootSettingsChanges(
                finalLocalizationParams.locale,
                fastBootSettings
            );
            if (changes.length === 0) {
                // No changes were detected so we can return the same promise.
                // However ensure that if the second call attempts to set runAfterBootLocalization
                // then do it.
                if (!runAfterBootLocalizationSet) {
                    // This is required as we may or may not pass runAfterBootLocalization early so the code
                    // should be able to attach this handler later to the same promise if the method is called again.
                    runAfterBootLocalizationSet = true;
                    bootLocalizationPromise = bootLocalizationPromise.then(() => {
                        runAfterBootLocalization?.();
                    });
                }
                return bootLocalizationPromise;
            }
        }

        fastBootSettings = fastBootSettings || getUserFastBootSettings();

        // If this part is executed, then it means either it is first call or changes were detected
        // and we need to override bootLocalizationPromise
        const wrappedLocalizationFunction = measureFunctionPerf(
            initializeBootLocalization,
            'localization'
        );

        bootLocalizationPromise = wrappedLocalizationFunction(
            finalLocalizationParams.locale,
            finalLocalizationParams.dir,
            workload,
            runAfterBootLocalization
        );

        traceBootstrapDiagnostics('bootLocalizationPromiseStarts');
        await bootLocalizationPromise;
        traceBootstrapDiagnostics('bootLocalizationPromiseEnds');

        if (changes?.length) {
            // Update is required as initialization of date time can only be done
            // once so update call needs to be made.
            updateDateTime(getUserConfiguration() as unknown as OwaUserConfiguration);
        }
    };
}

function initializeBootLocalization(
    locale: string,
    dir: 'ltr' | 'rtl',
    workload: OwaWorkload,
    runAfterBootLocalization?: () => void
) {
    // Initialize the strings coming from owa-strings
    initialize(urlResolve(getPackageBaseUrl(), 'resources/locale/'), locale, undefined);

    // Add string localization for any shared packages here
    // Don't initialize date-time and time-format localized strings for people module
    const localizationPromise = Promise.all([
        waitForCalendarStrings(workload),
        initializeTranslationsForOwaDateTime(),
    ]).then(() => {
        // Module specific callback which is triggered after localization is complete.
        if (runAfterBootLocalization) {
            runAfterBootLocalizationSet = true;
            runAfterBootLocalization();
        }
    });

    setDocumentDirection(locale, dir);

    return localizationPromise;
}

export default getInitializeBootLocalization;
